import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Interfaces para el servicio
export interface SparePartSummary {
  sparePartId: number;
  sparePartName: string;
  productCode: string;
  productStatus: string;
}

export interface PendingPackageOutModel {
  pendingPackageId: number;
  packageNumber: string;
  orderNumber: string;
  clienteUY?: string;
  status: string;
  createdAt: Date;
  assignedAt?: Date;
  createdBy?: string;
  sparePartCount: number;
  spareParts: SparePartSummary[];
}

export interface SearchPendingPackagesRequest {
  packageNumber?: string;
  orderNumber?: string;
  status?: string;
  fromDate?: Date;
  toDate?: Date;
  page?: number;
  pageSize?: number;
}

export interface SearchPendingPackagesResponse {
  pendingPackages: PendingPackageOutModel[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ScanPendingPackageRequest {
  packageNumber: string;
  listType: string; // "empaquetado", "enviado", "cargado"
  listName?: string;
  transportType?: string; // Requerido para tipo "cargado"
  scannedBy?: string;
  remitoNumber?: string; // Para tipo "enviado"
  selectedPackager?: string;
}

export interface ScanPendingPackageResponse {
  success: boolean;
  message: string;
  packageListId?: number;
  packageNumber: string;
  listName: string;
  sparePartsCount: number;
  addedItems: any[];
  emergentCommentAlerts?: EmergentCommentAlertGroup[];
}

export interface EmergentCommentAlertGroup {
  comment: string;
  items: EmergentCommentAlertItem[];
}

export interface EmergentCommentAlertItem {
  comment: string;
  sparePartId: number;
  sparePartName: string;
  productCode: string;
  packageNumber?: string;
  orderNumber?: string;
  triggerFields: string[];
}

export interface EmergentCommentAlertsResponse {
  emergentCommentAlerts: EmergentCommentAlertGroup[];
}

export interface CheckPackageNumberResponse {
  packageNumber: string;
  isDeleted: boolean;
  exists: boolean;
  status: string;
  message?: string;
  deletedAt?: Date;
  deletedBy?: string;
  reason?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PendingPackagesService {
  private apiUrl = `${environment.apiEndpoint}/api/PendingPackage`;

  constructor(private http: HttpClient) { }

  // Buscar paquetes pendientes
  searchPendingPackages(request: SearchPendingPackagesRequest): Observable<SearchPendingPackagesResponse> {
    let params = new HttpParams();
    
    if (request.packageNumber) {
      params = params.set('packageNumber', request.packageNumber);
    }
    if (request.orderNumber) {
      params = params.set('orderNumber', request.orderNumber);
    }
    if (request.status) {
      params = params.set('status', request.status);
    }
    if (request.fromDate) {
      params = params.set('fromDate', request.fromDate.toISOString());
    }
    if (request.toDate) {
      params = params.set('toDate', request.toDate.toISOString());
    }
    if (request.page) {
      params = params.set('page', request.page.toString());
    }
    if (request.pageSize) {
      params = params.set('pageSize', request.pageSize.toString());
    }

    return this.http.get<SearchPendingPackagesResponse>(this.apiUrl, { params });
  }

  // Escanear y asignar un paquete pendiente a una lista
  scanPendingPackage(request: ScanPendingPackageRequest): Observable<ScanPendingPackageResponse> {
    return this.http.post<ScanPendingPackageResponse>(`${this.apiUrl}/scan`, request);
  }

  // Verificar el estado de un número de paquete
  checkPackageNumber(packageNumber: string): Observable<CheckPackageNumberResponse> {
    return this.http.get<CheckPackageNumberResponse>(`${this.apiUrl}/check/${encodeURIComponent(packageNumber)}`);
  }

  getEmergentCommentAlerts(packageNumber: string): Observable<EmergentCommentAlertsResponse> {
    return this.http.get<EmergentCommentAlertsResponse>(
      `${this.apiUrl}/${encodeURIComponent(packageNumber)}/emergent-comment-alerts`
    );
  }

  // Obtener paquetes pendientes con filtros específicos
  getPendingPackagesByStatus(status: string, page: number = 1, pageSize: number = 20): Observable<SearchPendingPackagesResponse> {
    return this.searchPendingPackages({
      status: status,
      page: page,
      pageSize: pageSize
    });
  }

  // Obtener paquetes pendientes por número de orden
  getPendingPackagesByOrder(orderNumber: string): Observable<SearchPendingPackagesResponse> {
    return this.searchPendingPackages({
      orderNumber: orderNumber,
      page: 1,
      pageSize: 100
    });
  }

  // Verificar si un paquete puede ser escaneado
  canPackageBeScanned(packageNumber: string): Observable<boolean> {
    return new Observable(observer => {
      this.checkPackageNumber(packageNumber).subscribe({
        next: (response) => {
          const canScan = response.exists && response.status === 'CREADO' && !response.isDeleted;
          observer.next(canScan);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  // Obtener mensaje de estado para un paquete
  getPackageStatusMessage(packageNumber: string): Observable<string> {
    return new Observable(observer => {
      this.checkPackageNumber(packageNumber).subscribe({
        next: (response) => {
          let message = response.message || 'Estado desconocido';
          
          if (response.isDeleted) {
            message = `❌ ${message}`;
          } else if (response.status === 'CREADO') {
            message = `✅ ${message}`;
          } else if (response.status === 'ASIGNADO') {
            message = `ℹ️ ${message}`;
          } else {
            message = `⚠️ ${message}`;
          }
          
          observer.next(message);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  // Validar datos de escaneo según el tipo de lista
  validateScanRequest(request: ScanPendingPackageRequest): string[] {
    const errors: string[] = [];

    if (!request.packageNumber || request.packageNumber.trim() === '') {
      errors.push('El número de paquete es obligatorio');
    }

    if (!request.listType || !['empaquetado', 'enviado', 'cargado'].includes(request.listType)) {
      errors.push('El tipo de lista debe ser: empaquetado, enviado o cargado');
    }

    if (request.listType === 'cargado' && (!request.transportType || request.transportType.trim() === '')) {
      errors.push('El tipo de transporte es obligatorio para listas de cargado');
    }

    if (request.listType === 'enviado' && (!request.remitoNumber || request.remitoNumber.trim() === '')) {
      errors.push('El número de remito es obligatorio para listas de enviado');
    }

    return errors;
  }
}
